import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import FlowLoopChart from "../charts/FlowLoopChart";
import { pickAveragedFlowLoop } from "../../utils/results/flowLoops";

function formatStatLabel(stat, t) {
  const normalizedStat = stat === "median" ? "median" : "mean";

  return t(`results.flowLoops.stat.${normalizedStat}`, {
    defaultValue: normalizedStat === "median" ? "Median" : "Mean",
  });
}

function buildWindowBreathRange(windowLoop, averagedFlowLoop) {
  const windowIndex = Number.isFinite(windowLoop?.index) ? windowLoop.index : 0;
  const breathCount = Number.isFinite(windowLoop?.breathCount)
    ? windowLoop.breathCount
    : Number.isFinite(averagedFlowLoop?.window)
      ? averagedFlowLoop.window
      : 1;
  const step = Number.isFinite(averagedFlowLoop?.step)
    ? averagedFlowLoop.step
    : breathCount;
  const start = windowIndex * step + 1;
  const end = start + Math.max(1, breathCount) - 1;

  return {
    start,
    end,
  };
}

function WindowedFlowLoopsGrid({ averagedFlowLoop, t, windowedHeight }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {averagedFlowLoop.windows.map((windowLoop, index) => {
        const breathRange = buildWindowBreathRange(
          windowLoop,
          averagedFlowLoop,
        );

        return (
          <FlowLoopChart
            key={`${windowLoop.index}-${windowLoop.centerTimeSeconds ?? index}`}
            title={null}
            subtitle={t("results.flowLoops.windowBreathRange", {
              defaultValue: "Breaths {{start}}-{{end}}",
              start: breathRange.start,
              end: breathRange.end,
            })}
            points={windowLoop.points}
            height={windowedHeight}
            squareChart={true}
            xTitle={t("results.flowLoops.volumeAxis", {
              defaultValue: "Volume",
            })}
            yTitle={t("results.flowLoops.flowAxis", {
              defaultValue: "Flow",
            })}
            showControls={false}
            showQuadrants={false}
            zeroOnlyTicks={true}
          />
        );
      })}
    </div>
  );
}

function AveragedFlowLoopsSectionImpl({
  flowLoopSource,
  pooledHeight = 280,
  windowedHeight = 220,
}) {
  const { t } = useTranslation();
  const averagedFlowLoop = useMemo(
    () => pickAveragedFlowLoop(flowLoopSource),
    [flowLoopSource],
  );

  if (!averagedFlowLoop) {
    return null;
  }

  const statLabel = formatStatLabel(averagedFlowLoop.stat, t);

  if (averagedFlowLoop.mode === "windowed") {
    return (
      <div className="w-full">
        <WindowedFlowLoopsGrid
          averagedFlowLoop={averagedFlowLoop}
          t={t}
          windowedHeight={windowedHeight}
        />
      </div>
    );
  }

  return (
    <FlowLoopChart
      title={null}
      subtitle={t("results.flowLoops.averagedSummary", {
        defaultValue: "{{stat}} loop across {{count}} breaths",
        stat: statLabel,
        count: averagedFlowLoop.loopCount ?? "—",
      })}
      points={averagedFlowLoop.points}
      height={pooledHeight}
      squareChart={true}
      xTitle={t("results.flowLoops.volumeAxis", {
        defaultValue: "Volume",
      })}
      yTitle={t("results.flowLoops.flowAxis", {
        defaultValue: "Flow",
      })}
      showControls={false}
      showQuadrants={false}
      zeroOnlyTicks={true}
    />
  );
}

const AveragedFlowLoopsSection = memo(AveragedFlowLoopsSectionImpl);

export default AveragedFlowLoopsSection;
